"use client";

import { zu } from "zod_utilz";
import queryString from "query-string";
import { usePathname, useSearchParams } from "next/navigation";
import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowLeftIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";

import { Divider } from "@/components/ui/divider";
import { Slider } from "@/components/ui/slider";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { BuyPremiumConsultancy } from "@/app/_components/buy-premium-consultancy";
import { ProfitChart } from "@/app/_components/profit-chart";
import { ProfitTable } from "@/app/_components/profit-table";
import { ShareButton } from "@/app/_components/share-button";
import { PrintButton } from "@/app/_components/print-button";
import { Error } from "@/app/_components/error";
import { RequiredBuyPremiumButton } from "@/app/_components/required/buy-premium-button";
import { SaveViabilityCalculatorDialog } from "@/app/_components/viability-calculator/save-viability-calculator";
import { ViabilityCalculatorLoading } from "@/app/_components/viability-calculator/loading";
import { DeleteViabilityCalculatorDialog } from "@/app/_components/viability-calculator/delete-viability-calculator";

import {
  getTaxAuctionCommision,
  getTaxEscriture,
  getTaxFRJ,
  getTaxIPTU,
  getTaxITBI,
  getTaxRegister,
  getTaxRealStateAgent,
  getTaxGovIRPF,
  getTaxEscriturePercentage,
  getTaxITBIPercentage,
  getTaxRegisterPercentage,
  getTaxCondominium,
  bankLoanPercentage,
  calculateGeneralValues,
  annualInterestRatePercentage,
  getTaxFRJPercentage,
  getTaxConsultantService,
  getAuctionCommisionPercentage,
  hasAuctionCommision,
} from "@/helper/property";
import {
  convertPercentageToNormal,
  formatBRL,
  preprocessNumber,
} from "@/helper/currency";
import { getAppUrl } from "@/helper/url";

import { useViability } from "@/queries/use-viability";

import {
  viabilityPropertySchema,
  viabilityPropertyBaseSchema,
} from "@/schemas/viabilities";

import { monthsToSell, newId, realStateAgentPercentage } from "@/constants";

import type { Viability, ViabilityProperty } from "@/types";

import states from "@/static/states.json";
import { useUser } from "@clerk/nextjs";
import { getUserPlanByMetadata } from "@/helper/user/client";

const currencyValueParser = (value: string | undefined) => value || 0;

const getDisplayValue = (value: number | undefined) => {
  if (value === 0 || value === undefined) {
    return "";
  }
  return value;
};

const inputNumberProps = {
  type: "number",
  placeholder: "0",
  min: "0",
  max: "100",
  step: "0.1",
};

interface EstateTitleProps {
  children: React.ReactNode;
  title: string;
}

const EstateTitle = ({ children, title }: EstateTitleProps) => (
  <div className="flex flex-col gap-8">
    <div className="flex flex-col gap-2">
      <p className="font-semibold">{title}</p>
      <hr className="my-2" />
    </div>
    {children}
  </div>
);

interface FormLabelWithTooltipProps {
  label: string;
  tooltip: React.ReactNode;
}

const FormLabelWithTooltip = ({
  label,
  tooltip,
}: FormLabelWithTooltipProps) => (
  <div className="flex flex-row gap-2 items-center">
    <FormLabel>{label}</FormLabel>
    <Tooltip>
      <TooltipTrigger asChild>
        <InformationCircleIcon
          className="h-[14px] w-[14px]"
          aria-hidden="true"
        />
      </TooltipTrigger>
      <TooltipContent align="start" className="max-w-[300px]">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  </div>
);

export const validFormValues = (values: ViabilityProperty) => {
  const parsedValues = zu.partialSafeParse(viabilityPropertyBaseSchema, values);
  return { ...values, ...parsedValues.validData };
};

const Results = ({ values }: { values: ViabilityProperty }) => {
  // Parse the form values through the schema to apply all transformations
  const processedValues = validFormValues(values);

  // Current slider values (updated immediately during drag)
  const [prazoDeVendaMeses, setPrazoDeVendaMeses] = useState<number>(
    processedValues.prazo_de_venda_meses
  );
  const [valorArrematacao, setValorArrematacao] = useState<number>(
    processedValues.valor_arrematacao
  );

  // Values used for calculation (only updated when slider interaction ends)
  const [committedPrazoDeVendaMeses, setCommittedPrazoDeVendaMeses] =
    useState<number>(processedValues.prazo_de_venda_meses);
  const [committedValorArrematacao, setCommittedValorArrematacao] =
    useState<number>(processedValues.valor_arrematacao);

  // Calculate with committed values instead of current slider values
  const priceInfo = calculateGeneralValues({
    ...processedValues,
    prazo_de_venda_meses: committedPrazoDeVendaMeses,
    valor_arrematacao: committedValorArrematacao,
  });

  const graphData = priceInfo.projecao_de_lucro.map((item) => ({
    ...item,
    mes: `Mês ${item.mes}`,
  }));

  const tableData = priceInfo.projecao_de_lucro[committedPrazoDeVendaMeses - 1];

  // Commit current slider values when interaction ends
  const commitPrazoDeVendaMeses = (value: number) => {
    setCommittedPrazoDeVendaMeses(value);
  };

  const commitValorArrematacao = (value: number) => {
    setCommittedValorArrematacao(value);
  };

  return (
    <EstateTitle title="Projeção de lucros estimado">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-14 items-center justify-between">
          <div className="flex flex-col gap-2 w-full">
            <p className="text-sm">Prazo de venda (meses):</p>
            <Slider
              value={[prazoDeVendaMeses]}
              onValueChange={(value) => setPrazoDeVendaMeses(value[0])}
              onValueCommit={(value) => commitPrazoDeVendaMeses(value[0])}
              min={1}
              max={24}
              step={1}
            />
            <p className="text-sm text-muted-foreground">
              {prazoDeVendaMeses} meses até a venda.
            </p>
          </div>
          <div className="flex flex-col gap-2 w-full">
            <p className="text-sm">Arrematação (R$):</p>
            <Slider
              value={[valorArrematacao]}
              onValueChange={(value) => setValorArrematacao(value[0])}
              onValueCommit={(value) => commitValorArrematacao(value[0])}
              min={processedValues.valor_arrematacao}
              max={processedValues.valor_avaliacao * 1.2}
              step={1000}
            />
            <p className="text-sm text-muted-foreground">
              {formatBRL(valorArrematacao)} de arrematação/lance máximo.
            </p>
          </div>
        </div>
        <div className="border rounded-md overflow-hidden">
          <table className="w-full">
            <tbody className="[&>tr>td]:px-3 [&>tr>td]:py-2 [&>tr>td]:sm:w-1/2">
              <tr className="border-b text-sm bg-gray-50">
                <td className="text-muted-foreground flex items-center gap-2">
                  Lucro líquido (R$):{" "}
                  <Tooltip>
                    <TooltipTrigger>
                      <InformationCircleIcon
                        className="h-[14px] w-[14px]"
                        aria-hidden="true"
                      />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-80" align="start">
                      <p>
                        Valor final de lucro após subtrair todos os custos da
                        operação, incluindo impostos e despesas.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </td>
                <td className="border-l">
                  {formatBRL(tableData.valor_lucro_liquido_sozinho)}
                </td>
              </tr>
              <tr className="border-b text-sm">
                <td className="text-muted-foreground flex items-center gap-2">
                  ROI (%):{" "}
                  <Tooltip>
                    <TooltipTrigger>
                      <InformationCircleIcon
                        className="h-[14px] w-[14px]"
                        aria-hidden="true"
                      />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-80" align="start">
                      <p>
                        Retorno sobre Investimento - porcentagem que representa
                        quanto você ganhará em relação ao valor total investido.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </td>
                <td className="border-l">{tableData.roi_solo}%</td>
              </tr>
              <tr className="border-b text-sm bg-gray-50">
                <td className="text-muted-foreground flex items-center gap-2">
                  Gasto total (R$):{" "}
                  <Tooltip>
                    <TooltipTrigger>
                      <InformationCircleIcon
                        className="h-[14px] w-[14px]"
                        aria-hidden="true"
                      />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-80" align="start">
                      <p>
                        Soma de todos os gastos do investimento, incluindo o
                        valor de entrada, impostos, taxas, despesas de
                        manutenção e custos mensais acumulados.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </td>
                <td className="border-l">
                  {formatBRL(tableData.valor_gasto_total)}
                </td>
              </tr>
              <tr className="border-b text-sm">
                <td className="text-muted-foreground flex items-center gap-2">
                  Gasto mensal (R$):{" "}
                  <Tooltip>
                    <TooltipTrigger>
                      <InformationCircleIcon
                        className="h-[14px] w-[14px]"
                        aria-hidden="true"
                      />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-80" align="start">
                      <p>
                        Soma de todos os gastos mensais, incluindo impostos,
                        taxas, despesas de manutenção e custos mensais
                        acumulados.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </td>
                <td className="border-l">
                  {formatBRL(tableData.valor_gasto_mensal)}
                </td>
              </tr>
              <tr className="border-b text-sm bg-gray-50">
                <td className="text-muted-foreground flex items-center gap-2">
                  TIR a.a. (%):{" "}
                  <Tooltip>
                    <TooltipTrigger>
                      <InformationCircleIcon
                        className="h-[14px] w-[14px]"
                        aria-hidden="true"
                      />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-80" align="start">
                      <p>
                        Taxa Interna de Retorno anual - representa a taxa de
                        juros anual equivalente que iguala o investimento aos
                        retornos futuros.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </td>
                <td className="border-l">{tableData.tir_anual}%</td>
              </tr>
              <tr className="border-b text-sm">
                <td className="text-muted-foreground flex items-center gap-2">
                  TIR a.m. (%):{" "}
                  <Tooltip>
                    <TooltipTrigger>
                      <InformationCircleIcon
                        className="h-[14px] w-[14px]"
                        aria-hidden="true"
                      />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-80" align="start">
                      <p>
                        Taxa Interna de Retorno mensal - representa a taxa de
                        juros mensal equivalente ao retorno do investimento.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </td>
                <td className="border-l">{tableData.tir_mensal}%</td>
              </tr>
              <tr className="border-b text-sm bg-gray-50">
                <td className="text-muted-foreground flex items-center gap-2">
                  VPL (R$):{" "}
                  <Tooltip>
                    <TooltipTrigger>
                      <InformationCircleIcon
                        className="h-[14px] w-[14px]"
                        aria-hidden="true"
                      />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-80" align="start">
                      <p>
                        Valor Presente Líquido - representa o valor atual do
                        fluxo de caixa futuro descontado a uma taxa de juros
                        específica, menos o investimento inicial.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </td>
                <td className="border-l">{formatBRL(tableData.vpl)}</td>
              </tr>
              <tr className="text-sm">
                <td className="text-muted-foreground flex items-center gap-2">
                  Investindo à SELIC (R$):{" "}
                  <Tooltip>
                    <TooltipTrigger>
                      <InformationCircleIcon
                        className="h-[14px] w-[14px]"
                        aria-hidden="true"
                      />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-80" align="start">
                      <p>
                        Valor futuro se o mesmo investimento inicial e despesas
                        mensais fossem aplicados na taxa SELIC de 14,25% ao ano.
                        Permite comparar o retorno do imóvel com um investimento
                        de referência.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </td>
                <td className="border-l">
                  {formatBRL(tableData.valor_investimento_selic)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-10 mb-16 print:hidden">
          <ProfitChart
            data={graphData}
            className="w-[320px] sm:w-full h-[220px]"
          />
        </div>
        <div className="mt-4">
          <ProfitTable data={graphData} />
        </div>
      </div>
    </EstateTitle>
  );
};

export interface ViabilityCalculatorProps {
  hasPaidPlan?: boolean;
  avoidSave?: boolean;
  hideSave?: boolean;
  hideShare?: boolean;
  id?: string;
  data?: Partial<Viability>;
  onBack?: () => void;
  onSave?: () => void;
}

const ViabilityCalculatorPremium = (props: ViabilityCalculatorProps) => {
  const { id } = props;
  const isNew = id === newId;
  const { data, isFetching, error, refetch } = useViability(isNew ? "" : id!);

  if (isFetching) return <ViabilityCalculatorLoading />;

  if (error) return <Error reset={refetch} />;

  return <ViabilityCalculatorMain {...props} data={data || props.data} />;
};

const ViabilityCalculatorMain = ({
  hasPaidPlan,
  avoidSave,
  hideShare,
  hideSave,
  id,
  data,
  onBack,
  onSave,
}: ViabilityCalculatorProps) => {
  const resultsRef = useRef<HTMLDivElement>(null);
  const [visibleResult, setVisibleResult] = useState(false);
  const [IRMode, setIRMode] = useState<"auto" | "manual">("auto");
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const params = queryString.parse(searchParams.toString(), {
    parseNumbers: true,
    parseBooleans: true,
  }) as unknown as ViabilityProperty;

  const valorArrematacao = data?.valor_arrematacao || params?.valor_arrematacao;
  const valorAvaliacao = data?.valor_avaliacao || params?.valor_avaliacao;
  const modalidadeDeVenda =
    data?.modalidade_de_venda || params?.modalidade_de_venda;
  const uf = data?.uf || params?.uf;
  const tipo = data?.tipo || params?.tipo;
  const comissaoLeiloeiro =
    data?.comissao_leiloeiro || params?.comissao_leiloeiro;
  const comissaoLeiloeiroPorcentagem = getAuctionCommisionPercentage(
    modalidadeDeVenda,
    data?.comissao_leiloeiro_porcentagem ||
      params?.comissao_leiloeiro_porcentagem
  );
  const taxaITBIPorcentagem =
    data?.taxa_itbi_porcentagem ||
    params?.taxa_itbi_porcentagem ||
    getTaxITBIPercentage(uf, true);
  const taxaEscrituraPorcentagem =
    data?.taxa_escritura_porcentagem ||
    params?.taxa_escritura_porcentagem ||
    getTaxEscriturePercentage(uf, true);
  const taxaRegistroPorcentagem =
    data?.taxa_registro_porcentagem ||
    params?.taxa_registro_porcentagem ||
    getTaxRegisterPercentage(uf, true);
  const comissaoCorretorPorcentagem =
    data?.comissao_corretor_porcentagem ||
    params?.comissao_corretor_porcentagem ||
    convertPercentageToNormal(realStateAgentPercentage);
  const prazoDeVendaMeses =
    data?.prazo_de_venda_meses || params?.prazo_de_venda_meses || monthsToSell;

  const form = useForm<ViabilityProperty>({
    defaultValues: {
      valor_arrematacao:
        data?.valor_arrematacao ||
        params?.valor_arrematacao ||
        valorArrematacao,
      valor_avaliacao:
        data?.valor_avaliacao || params?.valor_avaliacao || valorAvaliacao,
      porcentagem_entrada:
        data?.porcentagem_entrada ||
        params?.porcentagem_entrada ||
        convertPercentageToNormal(
          bankLoanPercentage(data?.banco, modalidadeDeVenda)
        ),
      numero_de_parcelas_entrada:
        data?.numero_de_parcelas_entrada ||
        params?.numero_de_parcelas_entrada ||
        undefined,
      juros_anual:
        data?.juros_anual ||
        params?.juros_anual ||
        annualInterestRatePercentage(modalidadeDeVenda),
      assesoria_inicial:
        data?.assesoria_inicial || params?.assesoria_inicial || undefined,
      valor_outras_despesas_inicial:
        data?.valor_outras_despesas_inicial ||
        params?.valor_outras_despesas_inicial ||
        undefined,
      uf: data?.uf || params?.uf || uf,
      comissao_leiloeiro:
        comissaoLeiloeiro ||
        getTaxAuctionCommision(
          valorArrematacao,
          modalidadeDeVenda,
          comissaoLeiloeiroPorcentagem
        ),
      comissao_leiloeiro_porcentagem: comissaoLeiloeiroPorcentagem,
      taxa_itbi:
        data?.taxa_itbi ||
        params?.taxa_itbi ||
        getTaxITBI(valorArrematacao, uf, taxaITBIPorcentagem),
      taxa_itbi_porcentagem: taxaITBIPorcentagem,
      taxa_escritura:
        data?.taxa_escritura ||
        params?.taxa_escritura ||
        getTaxEscriture(valorArrematacao, uf, taxaEscrituraPorcentagem),
      taxa_escritura_porcentagem: taxaEscrituraPorcentagem,
      taxa_registro:
        data?.taxa_registro ||
        params?.taxa_registro ||
        getTaxRegister(valorArrematacao, uf, taxaRegistroPorcentagem),
      taxa_registro_porcentagem: taxaRegistroPorcentagem,
      taxa_frj:
        data?.taxa_frj || params?.taxa_frj || getTaxFRJ(valorArrematacao, uf),
      valor_desocupacao:
        data?.valor_desocupacao || params?.valor_desocupacao || undefined,
      dividas_propter_rem:
        data?.dividas_propter_rem || params?.dividas_propter_rem || undefined,
      valor_outras_despesas_regularizacao:
        data?.valor_outras_despesas_regularizacao ||
        params?.valor_outras_despesas_regularizacao ||
        undefined,
      valor_outras_despesas_venda:
        data?.valor_outras_despesas_venda ||
        params?.valor_outras_despesas_venda ||
        undefined,
      valor_reforma: data?.valor_reforma || params?.valor_reforma || undefined,
      comissao_corretor:
        data?.comissao_corretor ||
        params?.comissao_corretor ||
        getTaxRealStateAgent(valorAvaliacao),
      comissao_corretor_porcentagem: comissaoCorretorPorcentagem,
      assesoria_venda:
        data?.assesoria_venda || params?.assesoria_venda || undefined,
      assesoria_venda_porcentagem:
        data?.assesoria_venda_porcentagem ||
        params?.assesoria_venda_porcentagem ||
        undefined,
      trafego_pago: data?.trafego_pago || params?.trafego_pago || undefined,
      taxa_irpf:
        data?.taxa_irpf ||
        params?.taxa_irpf ||
        getTaxGovIRPF(valorAvaliacao, valorArrematacao),
      frequencia_iptu:
        data?.frequencia_iptu || params?.frequencia_iptu || "mensal",
      valor_iptu:
        data?.valor_iptu ||
        params?.valor_iptu ||
        getTaxIPTU(valorArrematacao, "mensal"),
      valor_condominio:
        data?.valor_condominio ||
        params?.valor_condominio ||
        getTaxCondominium(tipo),
      prazo_de_venda_meses: prazoDeVendaMeses,
      modalidade_de_venda: modalidadeDeVenda,
    },
    resolver: zodResolver(viabilityPropertySchema),
  });

  const formValues = form.getValues();

  const watchedUf = form.watch("uf");
  const watchedTotalPrice =
    Number(preprocessNumber(form.watch("valor_arrematacao"))) || 0;
  const watchedTotalValuation =
    Number(preprocessNumber(form.watch("valor_avaliacao"))) || 0;
  const watchedIptuFrequency = form.watch("frequencia_iptu");
  const watchedComissaoLeiloeiroPorcentagem = form.watch(
    "comissao_leiloeiro_porcentagem"
  );
  const watchedTaxaITBIPorcentagem = form.watch("taxa_itbi_porcentagem");
  const watchedTaxaEscrituraPorcentagem = form.watch(
    "taxa_escritura_porcentagem"
  );
  const watchedTaxaRegistroPorcentagem = form.watch(
    "taxa_registro_porcentagem"
  );
  const watchedComissaoCorretorPorcentagem = form.watch(
    "comissao_corretor_porcentagem"
  );
  const watchedAssesoriaVendaPorcentagem = form.watch(
    "assesoria_venda_porcentagem"
  );
  const watchedPorcentagemEntrada =
    Number(preprocessNumber(form.watch("porcentagem_entrada"))) || 0;

  useEffect(() => {
    form.setValue(
      "comissao_leiloeiro",
      getTaxAuctionCommision(
        watchedTotalPrice,
        modalidadeDeVenda,
        watchedComissaoLeiloeiroPorcentagem
      )
    );
    form.setValue(
      "taxa_itbi",
      getTaxITBI(watchedTotalPrice, watchedUf, watchedTaxaITBIPorcentagem)
    );
    form.setValue(
      "taxa_escritura",
      getTaxEscriture(
        watchedTotalPrice,
        watchedUf,
        watchedTaxaEscrituraPorcentagem
      )
    );
    form.setValue(
      "taxa_registro",
      getTaxRegister(
        watchedTotalPrice,
        watchedUf,
        watchedTaxaRegistroPorcentagem
      )
    );
    form.setValue("taxa_frj", getTaxFRJ(watchedTotalPrice, watchedUf));
  }, [
    watchedTotalPrice,
    watchedUf,
    watchedComissaoLeiloeiroPorcentagem,
    watchedTaxaITBIPorcentagem,
    watchedTaxaEscrituraPorcentagem,
    watchedTaxaRegistroPorcentagem,
  ]);

  useEffect(() => {
    form.setValue(
      "comissao_corretor",
      getTaxRealStateAgent(
        watchedTotalValuation,
        watchedComissaoCorretorPorcentagem
      )
    );

    form.setValue(
      "assesoria_venda",
      getTaxConsultantService(
        watchedTotalValuation,
        watchedAssesoriaVendaPorcentagem
      )
    );

    if (IRMode === "auto") {
      form.setValue(
        "taxa_irpf",
        getTaxGovIRPF(watchedTotalValuation, watchedTotalPrice)
      );
    }
  }, [
    watchedTotalPrice,
    watchedTotalValuation,
    watchedAssesoriaVendaPorcentagem,
    watchedComissaoCorretorPorcentagem,
    IRMode,
  ]);

  useEffect(() => {
    form.setValue(
      "valor_iptu",
      getTaxIPTU(watchedTotalPrice, watchedIptuFrequency)
    );
  }, [watchedTotalPrice, watchedIptuFrequency]);

  // useEffect(() => {
  //   if (watchedPorcentagemEntrada >= 100) {
  //     form.setValue("numero_de_parcelas_entrada", 0);
  //   }
  // }, [watchedPorcentagemEntrada, form]);

  const createShareUrl = () => {
    const processedValues = validFormValues(formValues);

    return `${getAppUrl()}${pathname}?${queryString.stringify(processedValues, {
      skipEmptyString: true,
      skipNull: true,
    })}`;
  };

  const submit = () => {
    if (hasPaidPlan) {
      setVisibleResult(true);

      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 0);
    }
  };

  const hasAuctionCommisionTax = hasAuctionCommision(modalidadeDeVenda);
  const isFinancing =
    watchedPorcentagemEntrada > 0 && watchedPorcentagemEntrada < 100;

  return (
    <div className="flex flex-col gap-12">
      {onBack ? (
        <div>
          <Button
            variant="ghost"
            className="flex items-center justify-center gap-2"
            onClick={onBack}
          >
            <ArrowLeftIcon width={18} height={18} />
            Voltar para lista de cálculos
          </Button>
        </div>
      ) : null}
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(submit)}
          className="flex flex-col gap-12"
        >
          <EstateTitle title="Premissas">
            <FormField
              control={form.control}
              name="valor_arrematacao"
              render={({ field }) => (
                <FormItem className="space-y-0 w-full flex flex-col gap-2">
                  <FormLabelWithTooltip
                    label="Valor de arrematação (R$)"
                    tooltip={
                      <p>
                        Esse é o valor mínimo que você pode arrematar o imóvel,
                        todos os cálculos são feitos com base nesse valor
                      </p>
                    }
                  />
                  <CurrencyInput
                    id="valor_arrematacao"
                    placeholder="R$ 0,00"
                    className="col-span-2 h-8"
                    onValueChange={(value) => {
                      field.onChange(currencyValueParser(value));
                    }}
                    value={getDisplayValue(field.value)}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="valor_avaliacao"
              render={({ field }) => (
                <FormItem className="space-y-0 w-full flex flex-col gap-2">
                  <FormLabelWithTooltip
                    label="Valor de avaliação (R$)"
                    tooltip={
                      <p>
                        Quase sempre o valor de avaliação pelo banco não é o
                        valor do imóvel real, por isso é importante analisar o
                        mercado e ajustar esse valor
                      </p>
                    }
                  />
                  <CurrencyInput
                    id="valor_avaliacao"
                    placeholder="R$ 0,00"
                    className="col-span-2 h-8"
                    onValueChange={(value) => {
                      field.onChange(currencyValueParser(value));
                    }}
                    value={getDisplayValue(field.value)}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex flex-row gap-4">
              <div className="flex flex-col sm:flex-row gap-4 w-full">
                <FormField
                  control={form.control}
                  name="porcentagem_entrada"
                  render={({ field }) => (
                    <FormItem className="space-y-0 w-full flex flex-col gap-2">
                      <FormLabel>Entrada (%)</FormLabel>
                      <FormControl>
                        <Input
                          {...inputNumberProps}
                          {...field}
                          value={getDisplayValue(field.value)}
                          onChange={(e) => {
                            const value = e.target.valueAsNumber || 0;
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* <FormField
                  control={form.control}
                  name="numero_de_parcelas_entrada"
                  render={({ field }) => (
                    <FormItem className="space-y-0 w-full flex flex-col gap-2">
                      <FormLabel>Número de parcelas</FormLabel>
                      <FormControl>
                        <Input
                          {...inputNumberProps}
                          {...field}
                          step={1}
                          max={undefined}
                          disabled={!isFinancing}
                          value={getDisplayValue(field.value)}
                          onChange={(e) => {
                            const value = e.target.valueAsNumber || 0;
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                /> */}
              </div>
              <FormField
                control={form.control}
                name="juros_anual"
                render={({ field }) => (
                  <FormItem className="space-y-0 w-full flex flex-col gap-2">
                    <FormLabel>Taxa de Juros Anual (%)</FormLabel>
                    <FormControl>
                      <Input
                        disabled={!isFinancing}
                        {...inputNumberProps}
                        {...field}
                        value={getDisplayValue(field.value)}
                        onChange={(e) => {
                          const value = e.target.valueAsNumber || 0;
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="assesoria_inicial"
              render={({ field }) => (
                <FormItem className="space-y-0 w-full flex flex-col gap-2">
                  <FormLabel>Assessoria (R$)</FormLabel>
                  <CurrencyInput
                    id="assesoria_inicial"
                    placeholder="R$ 0,00"
                    className="col-span-2 h-8"
                    onValueChange={(value) => {
                      field.onChange(currencyValueParser(value));
                    }}
                    value={getDisplayValue(field.value)}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="valor_outras_despesas_inicial"
              render={({ field }) => (
                <FormItem className="space-y-0 w-full flex flex-col gap-2">
                  <FormLabel className="text-muted-foreground">
                    Outros gastos (R$)
                  </FormLabel>
                  <CurrencyInput
                    id="valor_outras_despesas_inicial"
                    placeholder="R$ 0,00"
                    className="col-span-2 h-8"
                    onValueChange={(value) => {
                      field.onChange(currencyValueParser(value));
                    }}
                    value={getDisplayValue(field.value)}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </EstateTitle>
          <EstateTitle title="Regularização">
            <FormField
              control={form.control}
              name="uf"
              render={({ field }) => (
                <FormItem className="space-y-0 w-full flex flex-col gap-2">
                  <FormLabel>Estado</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o estado do imóvel" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {states
                        .sort((a, b) => a.pib - b.pib)
                        .map((item) => (
                          <SelectItem key={item.value} value={item.value}>
                            {item.label} -{" "}
                            <span className="uppercase">{item.value}</span>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex flex-row gap-4 items-center sm:justify-between">
              <FormField
                control={form.control}
                name="comissao_leiloeiro"
                render={({ field }) => (
                  <FormItem className="space-y-0 w-full flex flex-col gap-2">
                    <FormLabelWithTooltip
                      label="Comissão do leiloeiro (R$)"
                      tooltip={
                        <p>
                          A comissão do leiloeiro geralmente é de 5% do valor de
                          arrematação.
                        </p>
                      }
                    />
                    <FormControl>
                      <CurrencyInput
                        id="comissao_leiloeiro"
                        placeholder="R$ 0,00"
                        className="col-span-2 h-8"
                        disabled={!hasAuctionCommisionTax}
                        onValueChange={(value) => {
                          field.onChange(currencyValueParser(value));
                        }}
                        value={getDisplayValue(field.value)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <span className="text-muted-foreground mt-2">__</span>
              <FormField
                control={form.control}
                name="comissao_leiloeiro_porcentagem"
                render={({ field }) => (
                  <FormItem className="space-y-0 w-full flex flex-col gap-2">
                    <FormLabel className="text-muted-foreground">
                      Porcentagem (%)
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...inputNumberProps}
                        {...field}
                        disabled={!hasAuctionCommisionTax}
                        value={getDisplayValue(field.value)}
                        onChange={(e) => {
                          const value = e.target.valueAsNumber || 0;
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex flex-row gap-4 items-center sm:justify-between">
              <FormField
                control={form.control}
                name="taxa_itbi"
                render={({ field }) => (
                  <FormItem className="space-y-0 w-full flex flex-col gap-2">
                    <FormLabelWithTooltip
                      label="ITBI (R$)"
                      tooltip={
                        <div className="flex flex-col gap-4">
                          <p>
                            O ITBI é um imposto estadual que varia de 1% a 3% do
                            valor de arrematação.
                          </p>
                          <p>
                            No estado de{" "}
                            <span className="uppercase">{watchedUf}</span> o
                            ITBI é de {getTaxITBIPercentage(watchedUf, true)}%
                            do valor de arrematação.
                          </p>
                        </div>
                      }
                    />
                    <FormControl>
                      <CurrencyInput
                        id="taxa_itbi"
                        placeholder="R$ 0,00"
                        className="col-span-2 h-8"
                        onValueChange={(value) => {
                          field.onChange(currencyValueParser(value));
                        }}
                        value={getDisplayValue(field.value)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <span className="text-muted-foreground mt-2">__</span>
              <FormField
                control={form.control}
                name="taxa_itbi_porcentagem"
                render={({ field }) => (
                  <FormItem className="space-y-0 w-full flex flex-col gap-2">
                    <FormLabel className="text-muted-foreground">
                      Porcentagem (%)
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...inputNumberProps}
                        {...field}
                        value={getDisplayValue(field.value)}
                        onChange={(e) => {
                          const value = e.target.valueAsNumber || 0;
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex flex-row gap-4 items-center sm:justify-between">
              <FormField
                control={form.control}
                name="taxa_escritura"
                render={({ field }) => (
                  <FormItem className="space-y-0 w-full flex flex-col gap-2">
                    <FormLabelWithTooltip
                      label="Escritura (R$)"
                      tooltip={
                        <div className="flex flex-col gap-4">
                          <p>
                            A escritura é um imposto estadual que varia de 2% a
                            3% do valor de arrematação.
                          </p>
                          <p>
                            No estado de{" "}
                            <span className="uppercase">{watchedUf}</span> a
                            escritura é de{" "}
                            {getTaxEscriturePercentage(watchedUf, true)}% do
                            valor de arrematação.
                          </p>
                        </div>
                      }
                    />
                    <FormControl>
                      <CurrencyInput
                        id="taxa_escritura"
                        placeholder="R$ 0,00"
                        className="col-span-2 h-8"
                        onValueChange={(value) => {
                          field.onChange(currencyValueParser(value));
                        }}
                        value={getDisplayValue(field.value)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <span className="text-muted-foreground mt-2">__</span>
              <FormField
                control={form.control}
                name="taxa_escritura_porcentagem"
                render={({ field }) => (
                  <FormItem className="space-y-0 w-full flex flex-col gap-2">
                    <FormLabel className="text-muted-foreground">
                      Porcentagem (%)
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...inputNumberProps}
                        {...field}
                        value={getDisplayValue(field.value)}
                        onChange={(e) => {
                          const value = e.target.valueAsNumber || 0;
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex flex-row gap-4 items-center sm:justify-between">
              <FormField
                control={form.control}
                name="taxa_registro"
                render={({ field }) => (
                  <FormItem className="space-y-0 w-full flex flex-col gap-2">
                    <FormLabelWithTooltip
                      label="Registro (R$)"
                      tooltip={
                        <div className="flex flex-col gap-4">
                          <p>
                            O registro é um imposto estadual que varia de 2% a
                            3% do valor de arrematação.
                          </p>
                          <p>
                            No estado de{" "}
                            <span className="uppercase">{watchedUf}</span> o
                            registro é de{" "}
                            {getTaxRegisterPercentage(watchedUf, true)}% do
                            valor de arrematação.
                          </p>
                        </div>
                      }
                    />
                    <FormControl>
                      <CurrencyInput
                        id="taxa_registro"
                        placeholder="R$ 0,00"
                        className="col-span-2 h-8"
                        onValueChange={(value) => {
                          field.onChange(currencyValueParser(value));
                        }}
                        value={getDisplayValue(field.value)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <span className="text-muted-foreground mt-2">__</span>
              <FormField
                control={form.control}
                name="taxa_registro_porcentagem"
                render={({ field }) => (
                  <FormItem className="space-y-0 w-full flex flex-col gap-2">
                    <FormLabel className="text-muted-foreground">
                      Porcentagem (%)
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...inputNumberProps}
                        {...field}
                        value={getDisplayValue(field.value)}
                        onChange={(e) => {
                          const value = e.target.valueAsNumber || 0;
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
              <FormField
                control={form.control}
                name="taxa_frj"
                render={({ field }) => (
                  <FormItem className="space-y-0 w-full flex flex-col gap-2">
                    <FormLabelWithTooltip
                      label="FRJ (R$)"
                      tooltip={
                        <div className="flex flex-col gap-4">
                          <p>
                            O FRJ é um imposto estadual que varia de 0% a 3% do
                            valor de arrematação.
                          </p>
                          <p>
                            No estado de{" "}
                            <span className="uppercase">{watchedUf}</span> o FRJ
                            é de {getTaxFRJPercentage(watchedUf, true)}% do
                            valor de arrematação.
                          </p>
                        </div>
                      }
                    />
                    <FormControl>
                      <CurrencyInput
                        id="taxa_frj"
                        placeholder="R$ 0,00"
                        className="col-span-2 h-8"
                        onValueChange={(value) => {
                          field.onChange(currencyValueParser(value));
                        }}
                        value={getDisplayValue(field.value)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
              <FormField
                control={form.control}
                name="valor_desocupacao"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Desocupação (R$)</FormLabel>
                    <FormControl>
                      <CurrencyInput
                        id="valor_desocupacao"
                        placeholder="R$ 0,00"
                        className="col-span-2 h-8"
                        onValueChange={(value) => {
                          field.onChange(currencyValueParser(value));
                        }}
                        value={getDisplayValue(field.value)}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="dividas_propter_rem"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <div className="flex flex-row gap-2 items-center">
                      <FormLabel>Dívidas propter rem (R$)</FormLabel>
                      <div>
                        <BuyPremiumConsultancy>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 min-h-0 underline"
                          >
                            Saber mais
                          </Button>
                        </BuyPremiumConsultancy>
                      </div>
                    </div>
                    <FormControl>
                      <CurrencyInput
                        id="dividas_propter_rem"
                        placeholder="R$ 0,00"
                        className="col-span-2 h-8"
                        onValueChange={(value) => {
                          field.onChange(currencyValueParser(value));
                        }}
                        value={getDisplayValue(field.value)}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="valor_outras_despesas_regularizacao"
              render={({ field }) => (
                <FormItem className="space-y-0 w-full flex flex-col gap-2">
                  <FormLabel className="text-muted-foreground">
                    Outros gastos (R$)
                  </FormLabel>
                  <FormControl>
                    <CurrencyInput
                      id="valor_outras_despesas_regularizacao"
                      placeholder="R$ 0,00"
                      className="col-span-2 h-8"
                      onValueChange={(value) => {
                        field.onChange(currencyValueParser(value));
                      }}
                      value={getDisplayValue(field.value)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </EstateTitle>
          <EstateTitle title="Despesas com a venda">
            <FormField
              control={form.control}
              name="valor_reforma"
              render={({ field }) => (
                <FormItem className="space-y-0 w-full flex flex-col gap-2">
                  <FormLabel>Reforma (R$)</FormLabel>
                  <FormControl>
                    <CurrencyInput
                      id="valor_reforma"
                      placeholder="R$ 0,00"
                      className="col-span-2 h-8"
                      onValueChange={(value) => {
                        field.onChange(currencyValueParser(value));
                      }}
                      value={getDisplayValue(field.value)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
              <FormField
                control={form.control}
                name="comissao_corretor"
                render={({ field }) => (
                  <FormItem className="space-y-0 w-full flex flex-col gap-2">
                    <FormLabelWithTooltip
                      label="Comissão do corretor (R$)"
                      tooltip={
                        <p>
                          A comissão do corretor geralmente é de 5% do valor de
                          arrematação.
                        </p>
                      }
                    />
                    <FormControl>
                      <CurrencyInput
                        id="comissao_corretor"
                        placeholder="R$ 0,00"
                        className="col-span-2 h-8"
                        onValueChange={(value) => {
                          field.onChange(currencyValueParser(value));
                        }}
                        value={getDisplayValue(field.value)}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <span className="text-muted-foreground mt-2">__</span>
              <FormField
                control={form.control}
                name="comissao_corretor_porcentagem"
                render={({ field }) => (
                  <FormItem className="space-y-0 w-full flex flex-col gap-2">
                    <FormLabel className="text-muted-foreground">
                      Porcentagem (%)
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...inputNumberProps}
                        {...field}
                        value={getDisplayValue(field.value)}
                        onChange={(e) => {
                          const value = e.target.valueAsNumber || 0;
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
              <FormField
                control={form.control}
                name="assesoria_venda"
                render={({ field }) => (
                  <FormItem className="space-y-0 w-full flex flex-col gap-2">
                    <FormLabelWithTooltip
                      label="Assessoria (R$)"
                      tooltip={
                        <p>
                          A comissão do corretor geralmente é de 5% do valor de
                          arrematação.
                        </p>
                      }
                    />
                    <FormControl>
                      <CurrencyInput
                        id="assesoria_venda"
                        placeholder="R$ 0,00"
                        className="col-span-2 h-8"
                        onValueChange={(value) => {
                          field.onChange(currencyValueParser(value));
                        }}
                        value={getDisplayValue(field.value)}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <span className="text-muted-foreground mt-2">__</span>
              <FormField
                control={form.control}
                name="assesoria_venda_porcentagem"
                render={({ field }) => (
                  <FormItem className="space-y-0 w-full flex flex-col gap-2">
                    <FormLabel className="text-muted-foreground">
                      Porcentagem (%)
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...inputNumberProps}
                        {...field}
                        value={getDisplayValue(field.value)}
                        onChange={(e) => {
                          const value = e.target.valueAsNumber || 0;
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="trafego_pago"
              render={({ field }) => (
                <FormItem className="space-y-0 w-full flex flex-col gap-2">
                  <FormLabelWithTooltip
                    label="Trafégo pago (R$)"
                    tooltip={
                      <p>
                        Caso você opte por anunciar o imóvel em uma midia
                        social, você pode informar o valor gasto com isso.
                      </p>
                    }
                  />
                  <FormControl>
                    <CurrencyInput
                      id="trafego_pago"
                      placeholder="R$ 0,00"
                      className="col-span-2 h-8"
                      onValueChange={(value) => {
                        field.onChange(currencyValueParser(value));
                      }}
                      value={getDisplayValue(field.value)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="taxa_irpf"
              render={({ field }) => (
                <FormItem className="space-y-0 w-full flex flex-col gap-2">
                  <div className="flex flex-row gap-8">
                    <FormLabelWithTooltip
                      label="Imposto de renda (R$)"
                      tooltip={
                        <div className="flex flex-col gap-4">
                          <p>
                            De modo geral, o valor do Imposto de Renda sobre a
                            venda do imóvel é de 15% sobre a parcela de lucro
                            obtida na operação, mas, na verdade, a alíquota do
                            IR está relacionada ao tamanho do ganho de capital
                            obtido na negociação.
                          </p>
                          <ul className="list-disc list-inside flex flex-col gap-1">
                            <li>Até R$ 5 milhões, alíquota é 15%</li>
                            <li>
                              De R$ 5 milhões até R$ 10 milhões, alíquota é
                              17,50%
                            </li>
                            <li>
                              De R$ 10 milhões até R$ 30 milhões, alíquota é 20%
                            </li>
                            <li>Mais de R$ 30 milhões, alíquota é 22,50%</li>
                          </ul>
                          <p>
                            Importante ressaltar que, ao ultrapassar o ganho de
                            capital de R$ 5 milhões, o crescimento da alíquota
                            passa a ser gradual, como se fosse um cálculo
                            &quot;em escada&quot;.
                          </p>
                          <p>
                            Por exemplo, se o ganho de capital for de R$ 7
                            milhões, a alíquota de 17,50% só será aplicada sobre
                            montante que ultrapassar os R$ 5 milhões. A tabela
                            abaixo traz uma simulação desse cálculo.
                          </p>
                        </div>
                      }
                    />
                    <RadioGroup
                      onValueChange={(value) =>
                        setIRMode(value as "auto" | "manual")
                      }
                      value={IRMode}
                      className="flex gap-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem id="ir-auto" value="auto" />
                        <Label htmlFor="ir-auto">Pessoa Física</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem id="ir-manual" value="manual" />
                        <Label htmlFor="ir-manual">Manual</Label>
                      </div>
                    </RadioGroup>
                  </div>
                  <FormControl>
                    <CurrencyInput
                      id="taxaIRPF"
                      placeholder="R$ 0,00"
                      className="col-span-2 h-8"
                      disabled={IRMode === "auto"}
                      onValueChange={(value) => {
                        field.onChange(currencyValueParser(value));
                      }}
                      value={getDisplayValue(field.value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="valor_outras_despesas_venda"
              render={({ field }) => (
                <FormItem className="space-y-0 w-full flex flex-col gap-2">
                  <FormLabel className="text-muted-foreground">
                    Outros gastos (R$)
                  </FormLabel>
                  <FormControl>
                    <CurrencyInput
                      id="valor_outras_despesas_venda"
                      placeholder="R$ 0,00"
                      className="col-span-2 h-8"
                      onValueChange={(value) => {
                        field.onChange(currencyValueParser(value));
                      }}
                      value={getDisplayValue(field.value)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </EstateTitle>
          <EstateTitle title="Despesas mensais">
            <FormField
              control={form.control}
              name="valor_iptu"
              render={({ field }) => (
                <FormItem className="w-full">
                  <div className="flex flex-row gap-8">
                    <FormLabel className="min-w-32">
                      IPTU{" "}
                      {watchedIptuFrequency === "mensal" ? "mensal" : "anual"}{" "}
                      (R$)
                    </FormLabel>
                    <FormField
                      control={form.control}
                      name="frequencia_iptu"
                      render={({ field }) => (
                        <FormItem className="space-y-0 w-full flex flex-col gap-2">
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              value={field.value}
                              className="flex gap-2"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                  id="iptu-mensal"
                                  value="mensal"
                                />
                                <Label htmlFor="iptu-mensal">Mensal</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem id="iptu-anual" value="anual" />
                                <Label htmlFor="iptu-anual">Anual</Label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormControl>
                    <CurrencyInput
                      id="valorIPTU"
                      placeholder="R$ 0,00"
                      className="col-span-2 h-8"
                      onValueChange={(value) => {
                        field.onChange(currencyValueParser(value));
                      }}
                      value={getDisplayValue(field.value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="valor_condominio"
              render={({ field }) => (
                <FormItem className="space-y-0 w-full flex flex-col gap-2">
                  <FormLabel>Condomínio mensal (R$)</FormLabel>
                  <FormControl>
                    <CurrencyInput
                      id="valor_condominio"
                      placeholder="R$ 0,00"
                      className="col-span-2 h-8"
                      onValueChange={(value) => {
                        field.onChange(currencyValueParser(value));
                      }}
                      value={getDisplayValue(field.value)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </EstateTitle>
          <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-4 sticky w-full left-0 right-0 bottom-0 bg-white py-4">
            <div className="flex flex-row items-center justify-center gap-2 w-full sm:w-auto">
              {!hideShare ? (
                <RequiredBuyPremiumButton>
                  <ShareButton
                    size="sm"
                    title="Compartilhar cálculo"
                    copyUrl={createShareUrl()}
                  />
                </RequiredBuyPremiumButton>
              ) : null}
              <RequiredBuyPremiumButton>
                <PrintButton size="sm" />
              </RequiredBuyPremiumButton>
            </div>
            <Divider size="large" borderWidth={1} className="block sm:hidden" />
            <div className="flex flex-row items-center justify-center gap-2 w-full sm:w-auto">
              <Button
                type="reset"
                variant="ghost"
                onClick={() => {
                  setVisibleResult(false);
                  form.reset();
                }}
              >
                Limpar
              </Button>
              {id && id !== newId ? (
                <DeleteViabilityCalculatorDialog id={id} onSave={onSave} />
              ) : null}
              {!hideSave ? (
                <SaveViabilityCalculatorDialog
                  avoidSave={avoidSave}
                  data={formValues}
                  id={id}
                  leilaoId={data?.leilao_id}
                  titulo={data?.titulo}
                  onSave={onSave}
                >
                  <RequiredBuyPremiumButton>
                    <Button type="button" variant="secondary">
                      Salvar cálculo
                    </Button>
                  </RequiredBuyPremiumButton>
                </SaveViabilityCalculatorDialog>
              ) : null}
              <RequiredBuyPremiumButton enabled={hasPaidPlan}>
                <Button type="submit">Calcular</Button>
              </RequiredBuyPremiumButton>
            </div>
          </div>
        </form>
      </Form>
      <div ref={resultsRef} className="scroll-mt-36">
        {hasPaidPlan && visibleResult ? <Results values={formValues} /> : null}
      </div>
    </div>
  );
};

export { ViabilityCalculatorMain, ViabilityCalculatorPremium };
